import type { OrderBy } from '@/lib/graphql-query-builder';

/**
 * Scope note: doctor-certified sick leave (legacy Kosovo-only; now any organization
 * whose roles grant `hrm::time-off::sick-leave-*`), filed by HR on an
 * employee's behalf (Angular's DoctorLeaveRequestModalComponent). File
 * upload for the doctor's certificate is deferred (no file-storage
 * integration wired in this pass) -- the request still carries
 * IsDocumentSendByDr: true per the Angular source's fixed behavior for this
 * flow.
 *
 * This reuses the shared "LeaveRequest" schema (same entity owned by
 * hr-leave-manager/hr-my-leave), filtered to this sick-leave flow.
 *
 * Schema discrepancy flagged (not applied -- see module report): the real
 * backend endpoint this module ports (KosovoAttendance.Domain.Services
 * .KosovoLeaveScheduleService.GetKosovoSickLeaveList) filters by
 * `LeaveCategoryId $in [region-specific Sick Leave category ids]`, not by a
 * `Title` string match. Those category ids are environment-seeded data we
 * don't have here, so this port keeps filtering on Title (a real field on
 * the live LeaveRequest schema) as a working proxy, same as before.
 */
export const SICK_LEAVE_TITLE = 'Sick Leave';

export interface SickLeaveRequest {
  ItemId: string;
  Title: string;
  /** The employee the leave is for (the Blocks IAM user id). */
  UserId: string;
  DisplayName?: string;
  DepartmentName?: string;
  /** First and last leave day, date only (`YYYY-MM-DDT00:00:00.000Z`, both inclusive). */
  StartDate: string;
  EndDate: string;
  Reason?: string;
  Status: 'Pending' | 'Approved' | 'Rejected';
  IsDocumentSendByDr?: boolean;
  /** When the request was filed (UTC). */
  CreatedDate?: string;
  /** IANA zone of the organization the request was made in. */
  Timezone?: string;
}

export interface SickLeaveRequestsPagingResult {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  pageNo: number;
  items: SickLeaveRequest[];
}

export interface GetSickLeaveRequestsParams {
  pageNo: number;
  pageSize: number;
  filter?: Record<string, unknown>;
  /** Defaults to newest request first. */
  order?: OrderBy[];
}

/**
 * Body of the SICK_LEAVE_REQUEST workflow (`.claude/skills/blocks-workflow`). Dates are plain
 * "YYYY-MM-DD" (both inclusive); the workflow looks up the real Sick Leave category (by `Title`, see
 * `SICK_LEAVE_TITLE`), stamps the target employee's department and decides `IsDocumentSendByDr` from
 * the sick-leave-certificate rule, instead of the client computing any of it.
 */
export interface SickLeaveRequestWorkflowInput {
  /** The employee the sick leave is filed for (HR files it on their behalf). */
  userId: string;
  startDate: string;
  endDate: string;
  reason?: string;
}

/** The workflow's answer, reduced to what the UI needs: `Message` is the refusal reason. */
export interface SickLeaveWorkflowResult {
  IsSuccessful: boolean;
  Message: string;
}
